import { Alert, flash, SubmitButton, Switch } from "@xola/ui-kit";
import clsx from "clsx";
import { nullableString } from "common";
import { omit } from "lodash";
import { singular } from "pluralize";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { ListingType, ProductObject, Term } from "types";
import { object } from "yup";
import { api } from "../../api/api";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useInvalidate } from "../../hooks/useInvalidation";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { TermsForm } from "./TermsForm";

const validationSchema = object().shape({
    termsLegal: nullableString().required(),
    termsHuman: nullableString(),
});

export const ProductTerms = ({
    terms,
    productObject = ProductObject.EXPERIENCE,
}: {
    terms?: Term;
    productObject?: ListingType;
}) => {
    const { id: productId } = useParams<{ id?: ID }>();
    const history = useHistory();
    const invalidate = useInvalidate();
    const [shouldOverrideGlobalTerms, setShouldOverrideGlobalTerms] = useState(!!terms?.id);
    const productType = productObject === ProductObject.PACKAGE ? "packages" : "experiences";
    const termsUrl = `/${productType}/${productId}/terms`;
    const hasTermsId = !!terms?.id;
    const [initialShouldOverrideGlobalTerms] = useState(!!terms?.id);

    useEffect(() => {
        setShouldOverrideGlobalTerms(hasTermsId);
    }, [hasTermsId]);

    const { mutateAsync: save, isLoading: isSaving } = useMutation(async (payload: any) => {
        try {
            return payload?.id ? await api.put(termsUrl, payload) : await api.post(termsUrl, payload);
        } catch {
            flash.danger("Error updating terms");
            return null;
        }
    });

    const onSave = async (values: any) => {
        await save(omit(values, "isFormDirty"));

        if (!shouldOverrideGlobalTerms) {
            await deleteTerms();
        }

        invalidate(productType, productId);
        flash.success("Successfully updated terms");
    };

    const handleResetForm = (handleReset: () => void) => {
        handleReset();
        setShouldOverrideGlobalTerms(initialShouldOverrideGlobalTerms);
    };

    const { mutateAsync: deleteTerms, isLoading: isDeleting } = useMutation(async () => {
        try {
            return await api.delete(termsUrl);
        } catch {
            flash.danger("Error deleting terms");
            return null;
        }
    });

    const handleOverrideSwitchChange = async (value: boolean) => {
        setShouldOverrideGlobalTerms(value);
        if (!value) {
            await deleteTerms();
        }

        invalidate(productType, productId);
    };

    const handleGlobalTermsLinkClick = () => {
        history.push("/products/globalSettings/terms");
    };

    return (
        <ContentLayout isWide isDisabled={isSaving || isDeleting}>
            <XolaFormik
                enableReinitialize
                validateOnChange
                validateOnBlur={false}
                validationSchema={validationSchema}
                initialValues={{ ...terms, isFormDirty: false }}
                onSubmit={onSave}
            >
                {({ values, setFieldValue, handleSubmit, handleReset, isValid }) => {
                    return (
                        <>
                            <h2>Terms</h2>
                            <div className="divide-y divide-gray-light">
                                <ContentLayout.Section
                                    title="Terms and Conditions"
                                    description="Terms and Conditions description"
                                >
                                    <Alert color="primary" className="mb-5">
                                        {!shouldOverrideGlobalTerms && (
                                            <span>
                                                <span className="mr-1">This {singular(productType)} is using the</span>
                                                <a
                                                    className="cursor-pointer font-bold text-primary-dark"
                                                    onClick={handleGlobalTermsLinkClick}
                                                >
                                                    Global Terms and Conditions
                                                </a>
                                                <span className="mr-1">
                                                    . You can configure custom terms and conditions specific to this
                                                </span>
                                                {singular(productType)} by overriding the global settings below.
                                            </span>
                                        )}

                                        <div
                                            className={clsx(
                                                !shouldOverrideGlobalTerms && "mt-5",
                                                "flex items-center space-x-2",
                                            )}
                                        >
                                            <Switch
                                                size="medium"
                                                isChecked={shouldOverrideGlobalTerms}
                                                onChange={(value: boolean) => {
                                                    handleOverrideSwitchChange(value);
                                                    setFieldValue("isFormDirty", true);
                                                }}
                                            />
                                            <h5>Override global terms and conditions</h5>
                                        </div>
                                    </Alert>

                                    {shouldOverrideGlobalTerms && (
                                        <TermsForm values={values} setFieldValue={setFieldValue} />
                                    )}
                                </ContentLayout.Section>

                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton
                                        disabled={shouldOverrideGlobalTerms && !isValid}
                                        size="medium"
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </SubmitButton>

                                    <SecondaryButton type="reset" onClick={() => handleResetForm(handleReset)}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>

                                <ScrollToFieldError />
                            </div>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};
